import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from "@/components/ui/table";
import { formatDate } from "@/lib/dateHelpers";

/**
 * Editable days table used in wizard step 3 and voyage detail.
 * Props:
 *   days: array of {day_number, date, port_or_sea, port_name, arrival_time,
 *                   departure_time, notes}
 *   onChange: (updatedDays) => void   — if null/undefined, table is read-only
 *   compact: boolean — smaller styling for wizard
 */
export default function DaysTable({ days, onChange, compact }) {
  if (!days || days.length === 0) return null;

  const editable = typeof onChange === "function";

  const updateDay = (idx, field, value) => {
    if (!editable) return;
    const updated = days.map((d, i) =>
      i === idx ? { ...d, [field]: value } : d
    );
    onChange(updated);
  };

  return (
    <div className="overflow-x-auto rounded-lg border" data-testid="days-table">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]">#</TableHead>
            <TableHead className="w-[120px]">Date</TableHead>
            <TableHead className="w-[100px]">Type</TableHead>
            <TableHead>Port Name</TableHead>
            <TableHead className="w-[110px]">Arrival</TableHead>
            <TableHead className="w-[110px]">Departure</TableHead>
            <TableHead>Notes</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {days.map((day, idx) => (
            <TableRow key={day.day_number} data-testid={`voyage-day-row-${day.day_number}`}>
              <TableCell className="font-mono text-xs text-muted-foreground">
                {day.day_number}
              </TableCell>
              <TableCell className="text-sm">
                {formatDate(day.date)}
              </TableCell>
              <TableCell>
                {editable ? (
                  <Select
                    value={day.port_or_sea || "Sea"}
                    onValueChange={(v) => updateDay(idx, "port_or_sea", v)}
                  >
                    <SelectTrigger className={compact ? "h-8 text-xs" : ""} data-testid={`day-type-${day.day_number}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Sea">Sea</SelectItem>
                      <SelectItem value="Port">Port</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <span className={day.port_or_sea === "Port" ? "text-primary font-medium" : "text-muted-foreground"}>
                    {day.port_or_sea}
                  </span>
                )}
              </TableCell>
              <TableCell>
                {editable ? (
                  <Input
                    value={day.port_name || ""}
                    onChange={(e) => updateDay(idx, "port_name", e.target.value || null)}
                    placeholder={day.port_or_sea === "Port" ? "Port name" : "—"}
                    className={compact ? "h-8 text-xs" : ""}
                    data-testid={`day-port-${day.day_number}`}
                  />
                ) : (
                  <span>{day.port_name || "—"}</span>
                )}
              </TableCell>
              <TableCell>
                {editable ? (
                  <Input
                    type="time"
                    value={day.arrival_time || ""}
                    onChange={(e) => updateDay(idx, "arrival_time", e.target.value || null)}
                    className={compact ? "h-8 text-xs" : ""}
                    data-testid={`day-arrival-${day.day_number}`}
                  />
                ) : (
                  <span>{day.arrival_time || "—"}</span>
                )}
              </TableCell>
              <TableCell>
                {editable ? (
                  <Input
                    type="time"
                    value={day.departure_time || ""}
                    onChange={(e) => updateDay(idx, "departure_time", e.target.value || null)}
                    className={compact ? "h-8 text-xs" : ""}
                    data-testid={`day-departure-${day.day_number}`}
                  />
                ) : (
                  <span>{day.departure_time || "—"}</span>
                )}
              </TableCell>
              <TableCell>
                {editable ? (
                  <Input
                    value={day.notes || ""}
                    onChange={(e) => updateDay(idx, "notes", e.target.value)}
                    placeholder="Notes"
                    className={compact ? "h-8 text-xs" : ""}
                    data-testid={`day-notes-${day.day_number}`}
                  />
                ) : (
                  <span className="text-xs text-muted-foreground">{day.notes || ""}</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
